import type { Ref, WritableComputedRef } from 'vue';
import { computed } from 'vue';

/**
 * Creates writable computed properties that batch updates to a parent model.
 *
 * This is needed when you have multiple child components that need to update
 * different properties of a parent object independently. Without batching, each property
 * update would trigger a separate reactive update, potentially overwriting other updates.
 *
 * By batching updates in a microtask, all property changes that occur in the same tick
 * are merged into a single update to the parent model, ensuring efficient reactivity.
 *
 * @example
 * ```ts
 * const modelValue = defineModel<RulesPolicyDocument>('modelValue', { required: true });
 * const { createWritableComputed } = useModelUpdater(modelValue);
 *
 * const itemRules = createWritableComputed('itemRules');
 * const fieldRules = createWritableComputed('fieldRules');
 *
 * // Both updates will be batched into a single parent update
 * itemRules.value = newItemRules;
 * fieldRules.value = newFieldRules;
 * ```
 */

export function useModelUpdater<T extends Record<string, any>>(modelValue: Ref<T>) {
	let pendingUpdates: Partial<T> = {};
	let scheduled = false;

	function scheduleUpdate<K extends keyof T>(key: K, value: T[K]) {
		pendingUpdates[key] = value;

		if (!scheduled) {
			scheduled = true;

			queueMicrotask(() => {
				modelValue.value = { ...modelValue.value, ...pendingUpdates };
				pendingUpdates = {};
				scheduled = false;
			});
		}
	}

	function createWritableComputed<K extends keyof T>(key: K): WritableComputedRef<T[K]> {
		return computed<T[K]>({
			get: () => modelValue.value[key],
			set: (value) => scheduleUpdate(key, value),
		});
	}

	return { createWritableComputed };
}
